import { BrowserRouter, Routes, Route, Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../core/services/supabase';
import { AuthProvider, useAuth } from '../core/contexts/AuthContext';
import { ModuleProvider } from '../core/contexts/ModuleContext';
import { usePermissions } from '../core/hooks/usePermissions';
import ProtectedRoute, { RequireAuth } from '../core/components/ProtectedRoute';
import { getUserDisplayName } from '../core/utils/authHelpers';
import ErrorBoundary from '../core/components/ErrorBoundary';
import Spinner from '../shared/components/ui/Spinner';
import NotificationBanner from '../components/NotificationBanner';
import OfflineBanner from '../core/components/OfflineBanner';
import LanguageSwitcher from '../core/components/LanguageSwitcher';

// Lazy-loaded module pages (code-splitting for better performance)
const HomePage = lazy(() => import('../modules/team-matches/pages/HomePage'));
const CaptainDashboard = lazy(() => import('../modules/team-matches/pages/CaptainDashboard'));
const CaptainLayout = lazy(() => import('../modules/team-matches/pages/CaptainLayout'));
const HomeTab = lazy(() => import('../modules/team-matches/pages/HomeTab'));
const MatchesTab = lazy(() => import('../modules/team-matches/pages/MatchesTab'));
const PlayersTab = lazy(() => import('../modules/team-matches/pages/PlayersTab'));
const SettingsTab = lazy(() => import('../modules/team-matches/pages/SettingsTab'));
const PlayerLayout = lazy(() => import('../modules/team-matches/pages/PlayerLayout'));
const PlayerMatchesTab = lazy(() => import('../modules/team-matches/pages/PlayerMatchesTab'));
const PlayerStatisticsTab = lazy(() => import('../modules/team-matches/pages/PlayerStatisticsTab'));
const PlayerLadderTab = lazy(() => import('../modules/team-matches/pages/PlayerLadderTab'));
const PlayerTournamentsTab = lazy(() => import('../modules/club-tournaments/pages/PlayerTournamentsTab'));
const PlayerProfileTab = lazy(() => import('../modules/team-matches/pages/PlayerProfileTab'));
const MatchDetail = lazy(() => import('../modules/team-matches/pages/MatchDetail'));
const SeasonDetail = lazy(() => import('../modules/team-matches/pages/SeasonDetail'));
const SystemAdminLayout = lazy(() => import('../admin/pages/SystemAdminLayout'));
const SystemAdminHomeTab = lazy(() => import('../admin/pages/SystemAdminHomeTab'));
const SystemAdminClubsTab = lazy(() => import('../admin/pages/SystemAdminClubsTab'));
const SystemAdminSettingsTab = lazy(() => import('../admin/pages/SystemAdminSettingsTab'));

// Club Admin PWA pages
const ClubAdminLayout = lazy(() => import('../modules/club-admin/pages/ClubAdminLayout'));
const ClubAdminTeamsTab = lazy(() => import('../modules/club-admin/pages/ClubAdminTeamsTab'));
const ClubAdminMembersTab = lazy(() => import('../modules/club-admin/pages/ClubAdminMembersTab'));
const ClubAdminAuditTab = lazy(() => import('../modules/club-admin/pages/ClubAdminAuditTab'));
const ClubTournamentsTab = lazy(() => import('../modules/club-tournaments/pages/ClubTournamentsTab'));
const TournamentRegistrationPage = lazy(() => import('../modules/club-tournaments/pages/TournamentRegistrationPage'));
const DevUtilities = lazy(() => import('./DevUtilities'));

// Tennis Ladder module pages
const ClubLaddersTab = lazy(() => import('../modules/tennis-ladder/pages/ClubLaddersTab'));
const LadderPage = lazy(() => import('../modules/tennis-ladder/pages/LadderPage'));
const LadderAdminDashboard = lazy(() => import('../modules/tennis-ladder/pages/LadderAdminDashboard'));
const LadderJoinPage = lazy(() => import('../modules/tennis-ladder/pages/LadderJoinPage.jsx'));

// Auth pages loaded immediately (small, critical for initial access)
import LoginPage from './LoginPage';
import AuthCallback from './AuthCallback';
import SetupPage from './SetupPage';
import UnauthorizedPage from './UnauthorizedPage';
import LandingPage from './LandingPage';
import SetPasswordPage from './SetPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import ChangePasswordPage from './ChangePasswordPage';

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const permissions = usePermissions();
  const { t } = useTranslation('common');

  // Hide navigation on landing page, login, setup, and auth callback pages
  if (['/', '/login', '/set-password', '/reset-password', '/change-password', '/setup', '/auth/callback', '/unauthorized'].includes(location.pathname)) {
    return null;
  }

  // Hide global navigation for PWA routes (each has its own top bar)
  const isCaptainPWA = location.pathname.match(/^\/team\/[^/]+\/captain/);
  const isPlayerPWA = location.pathname.match(/^\/team\/[^/]+\/player/)
    || location.pathname.match(/^\/player\/[^/]+/)
    || location.pathname.match(/^\/club\/[^/]+\/player/)
    || (location.pathname.match(/^\/match\//) && new URLSearchParams(location.search).has('token'));
  const isClubAdminPWA = location.pathname.match(/^\/club\/[^/]+/);
  const isSystemAdmin = location.pathname.startsWith('/admin');

  if (isCaptainPWA || isPlayerPWA || isClubAdminPWA || isSystemAdmin) {
    return null;
  }


  const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

  // Get user's highest role for display
  const getUserRole = () => {
    if (!permissions.userRoles || permissions.userRoles.length === 0) {
      return null;
    }

    const roleHierarchy = {
      system_admin: t('roles.systemAdmin'),
      club_admin: t('roles.clubAdmin'),
      team_captain: t('roles.teamCaptain'),
      player: t('roles.player'),
    };

    // Find highest role
    const roles = ['system_admin', 'club_admin', 'team_captain', 'player'];
    for (const role of roles) {
      if (permissions.userRoles.some(r => r.role === role)) {
        return roleHierarchy[role];
      }
    }

    return null;
  };

  const handleSignOut = async () => {
    navigate('/');
    await signOut();
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-bold text-gray-900">
                🎾 Tennis Team Manager
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              {!isCaptainPWA && (
                <Link
                  to="/matches"
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-sm"
                >
                  {t('navigation.link.matches')}
                </Link>
              )}
              <LanguageSwitcher />
              {(isDevelopment || permissions.isSystemAdmin()) && (
                <Link
                  to="/dev"
                  className="text-white hover:bg-yellow-700 px-3 py-2 rounded text-sm bg-yellow-600"
                  title="Developer Utilities - Create invitations without email"
                >
                  🔧 Dev
                </Link>
              )}
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-sm"
                >
                  {t('navigation.link.signOut')}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-sm"
                >
                  {t('navigation.link.signIn')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* User status bar */}
      {user && (
        <div className="bg-indigo-600 text-white border-b border-green-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="text-sm">
              <span className="font-medium">
                {t('navigation.userStatus.text.greeting', { name: getUserDisplayName(user) })}
              </span>
              {getUserRole() && (
                <span className="ml-2 text-green-100">
                  {' '}{t('navigation.userStatus.text.loggedInAs', { role: getUserRole() })}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Loading component for lazy-loaded pages
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModuleProvider>
          <div className="min-h-screen bg-gray-50">
            <OfflineBanner />
            <NotificationBanner />
            <ErrorBoundary>
              <Navigation />
            </ErrorBoundary>

            <main>
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/matches" element={<HomePage />} />
                    <Route path="/match/:matchId" element={<MatchDetail />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/set-password" element={<SetPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/change-password" element={<ChangePasswordPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />

                    {/* Season route (protected) */}
                    <Route
                      path="/season/:seasonId"
                      element={
                        <ProtectedRoute requireRole={['team_captain', 'club_admin', 'system_admin']}>
                          <SeasonDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Developer utilities (only accessible in dev mode) */}
                    <Route path="/dev" element={<DevUtilities />} />

                    {/* Legacy player token access — PWA layout */}
                    <Route path="/player/:token" element={<PlayerLayout />}>
                      <Route index element={<PlayerMatchesTab />} />
                      <Route path="statistics" element={<PlayerStatisticsTab />} />
                      <Route path="ladder" element={<PlayerLadderTab />} />
                      <Route path="tournaments" element={<PlayerTournamentsTab />} />
                      <Route path="profile" element={<PlayerProfileTab />} />
                    </Route>

                    {/* Setup page — accessible without auth for first-run bootstrap */}
                    <Route path="/setup" element={<SetupPage />} />

                    {/* System Admin routes — PWA layout with bottom navigation */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requireRole="system_admin">
                          <SystemAdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<SystemAdminHomeTab />} />
                      <Route path="clubs" element={<SystemAdminClubsTab />} />
                      <Route path="settings" element={<SystemAdminSettingsTab />} />
                    </Route>

                    {/* Club Admin routes — PWA layout with bottom navigation */}
                    <Route
                      path="/club/:clubId"
                      element={
                        <ProtectedRoute requireRole={['club_admin', 'system_admin']}>
                          <ClubAdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<ClubAdminTeamsTab />} />
                      <Route path="members" element={<ClubAdminMembersTab />} />
                      <Route path="ladders" element={<ClubLaddersTab />} />
                      <Route path="tournaments" element={<ClubTournamentsTab />} />
                      <Route path="activity" element={<ClubAdminAuditTab />} />
                    </Route>

                    {/* Team Captain routes — PWA layout with bottom navigation */}
                    <Route
                      path="/team/:teamId/captain"
                      element={
                        <ProtectedRoute requireRole={['team_captain', 'club_admin', 'system_admin']}>
                          <CaptainLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<HomeTab />} />
                      <Route path="matches" element={<MatchesTab />} />
                      <Route path="players" element={<PlayersTab />} />
                      <Route path="settings" element={<SettingsTab />} />
                    </Route>

                    {/* Legacy captain route (uses old monolithic dashboard) */}
                    <Route
                      path="/captain"
                      element={
                        <ProtectedRoute requireRole={['team_captain', 'club_admin', 'system_admin']}>
                          <CaptainDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Player routes — team-scoped PWA */}
                    <Route
                      path="/team/:teamId/player"
                      element={
                        <ProtectedRoute requireRole={['player', 'team_captain', 'club_admin', 'system_admin']}>
                          <PlayerLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<PlayerMatchesTab />} />
                      <Route path="statistics" element={<PlayerStatisticsTab />} />
                      <Route path="ladder" element={<PlayerLadderTab />} />
                      <Route path="tournaments" element={<PlayerTournamentsTab />} />
                      <Route path="profile" element={<PlayerProfileTab />} />
                    </Route>

                    {/* Player routes — club-scoped PWA (no team required) */}
                    <Route
                      path="/club/:clubId/player"
                      element={
                        <RequireAuth>
                          <PlayerLayout />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<PlayerMatchesTab />} />
                      <Route path="statistics" element={<PlayerStatisticsTab />} />
                      <Route path="ladder" element={<PlayerLadderTab />} />
                      <Route path="tournaments" element={<PlayerTournamentsTab />} />
                      <Route path="profile" element={<PlayerProfileTab />} />
                    </Route>

                    {/* Public tournament registration (token link) */}
                    <Route path="/register/tournament/:token" element={<TournamentRegistrationPage />} />

                    {/* Public ladder invitation join page */}
                    <Route path="/ladder-invite/:token" element={<LadderJoinPage />} />

                    {/* Tennis Ladder routes */}
                    <Route path="/ladder/:ladderId" element={<LadderPage />} />
                    <Route
                      path="/ladder/:ladderId/admin"
                      element={
                        <ProtectedRoute requireRole={['club_admin', 'system_admin']}>
                          <LadderAdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </main>
          </div>
        </ModuleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
